import { useNodeId } from "@xyflow/react";
import { Suspense, useRef, useCallback } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { Icon, Text } from "@zapier/design-system";
import { Typeahead, type Key } from "@zapier/design-system-beta";
import {
  interfacesPagesAsyncAtomFamily,
  interfacePageAsyncAtomFamily,
  takeSnapshotAtom,
} from "@zapier/canvas-state";
import { useFocusInput } from "../../../hooks/useFocusInput";
import useUpdateNode from "../../../hooks/useUpdateNode";
import { ErrorBoundary } from "../../ErrorBoundary";
import { SelectedItem } from "../SelectedItem";
import styles from "./FloatingPagesTypeahead.module.css";

type Props = {
  interfaceProjectId: string;
  interfacePageId: string | undefined;
};

function PagesTypeaheadLoader() {
  return (
    <div className={styles.floatingTypeaheadWrapper}>
      <Typeahead
        aria-label="Search interface pages"
        isBusy={true}
        size="medium"
        defaultSelectedKey="loading"
      >
        <Typeahead.Item key="loading" id="loading">
          Loading pages...
        </Typeahead.Item>
      </Typeahead>
    </div>
  );
}

function SelectedPageSuspended({ interfaceProjectId, interfacePageId }: Props) {
  const nodeId = useNodeId()!;
  const takeSnapshot = useSetAtom(takeSnapshotAtom);
  const updateNodeAndSave = useUpdateNode();
  const selectedPage = useAtomValue(
    interfacePageAsyncAtomFamily({
      interfaceProjectId,
      interfacePageId,
      nodeId,
    }),
  );

  const onRemove = useCallback(() => {
    takeSnapshot();
    updateNodeAndSave({
      data: {
        pageId: undefined,
      },
    });
  }, [takeSnapshot, updateNodeAndSave]);

  return (
    <SelectedItem
      label={selectedPage?.name || "Page not found"}
      icon={<Icon name="navInterfaces" size={26} color="BrandOrange" />}
      clearSelection={onRemove}
    />
  );
}

function PagesTypeaheadSuspended({
  interfaceProjectId,
}: Omit<Props, "interfacePageId">) {
  const inputRef = useRef<HTMLInputElement>(null);
  const takeSnapshot = useSetAtom(takeSnapshotAtom);
  const updateNodeAndSave = useUpdateNode();
  const pages = useAtomValue(
    interfacesPagesAsyncAtomFamily(interfaceProjectId),
  );

  useFocusInput(inputRef);

  const handleSelectionChange = useCallback(
    (key: Key | null) => {
      takeSnapshot();
      updateNodeAndSave({
        data: {
          pageId: key,
        },
      });

      if (key) {
        // TODO: emitEventOnInterfacePageConnect
      }
    },
    [takeSnapshot, updateNodeAndSave],
  );

  return (
    <div className={styles.floatingTypeaheadWrapper}>
      <Typeahead
        aria-label="Search interface pages"
        placeholder="Search interface pages"
        ref={inputRef}
        onSelectionChange={handleSelectionChange}
        classNamePopOver={styles.popover}
        size="medium"
      >
        {pages.map((page) => {
          return (
            <Typeahead.Item
              key={page.id}
              id={page.id}
              textValue={page.name}
              className={styles.floatingTypeaheadItem}
              size="medium"
            >
              <Icon name="navInterfaces" size={22} color="BrandOrange" />
              <div className={styles.truncateText}>
                <Text type="SmallPrint2">{page.name}</Text>
              </div>
            </Typeahead.Item>
          );
        })}
      </Typeahead>
    </div>
  );
}

export function FloatingPagesTypeahead({
  interfaceProjectId,
  interfacePageId,
}: Props) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PagesTypeaheadLoader />}>
        {interfacePageId ? (
          <SelectedPageSuspended
            interfaceProjectId={interfaceProjectId}
            interfacePageId={interfacePageId}
          />
        ) : (
          <PagesTypeaheadSuspended interfaceProjectId={interfaceProjectId} />
        )}
      </Suspense>
    </ErrorBoundary>
  );
}
